<main class="setup-shell">
  <aside class="setup-aside">
    <img class="passbook-logo" src="/passbook-logo.png" alt="Passbook" />

    <div class="aside-content">
      <span class="eyebrow">Business setup</span>
      <h1>Tell us about your jewellery business.</h1>
      <p>Just the essential details needed to create your business and primary store.</p>

      <div class="setup-points">
        <div>
          <span class="point-icon"><mat-icon>business</mat-icon></span>
          <div>
            <strong>Business details</strong>
            <p>Add your display name, legal entity, and address.</p>
          </div>
        </div>
        <div>
          <span class="point-icon"><mat-icon>storefront</mat-icon></span>
          <div>
            <strong>Store address</strong>
            <p>Reuse the business address or provide a different store location.</p>
          </div>
        </div>
        <div>
          <span class="point-icon"><mat-icon>verified_user</mat-icon></span>
          <div>
            <strong>Secure setup</strong>
            <p>Your information is kept private and protected.</p>
          </div>
        </div>
      </div>
    </div>

    <p class="aside-footer"><mat-icon>schedule</mat-icon> Takes about 2 minutes</p>
  </aside>

  <section class="form-panel">
    <form class="setup-card" [formGroup]="form" (ngSubmit)="submit()" novalidate>
      <header class="form-header">
        <div>
          <span class="eyebrow">Business profile</span>
          <h2>Set up your business</h2>
          <p>Complete the form below to continue.</p>
        </div>
        <div class="header-actions">
          <span class="secure-badge"><mat-icon>lock</mat-icon> Secure</span>
          <button class="logout-button" type="button" (click)="logout()">
            <mat-icon>logout</mat-icon>
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div class="form-scroll">
        <section class="form-section">
          <div class="section-title">
            <span><mat-icon>business</mat-icon></span>
            <div>
              <h3>Business information</h3>
              <p>Enter your registered business details.</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="field">
              <label class="required-label" for="businessName">Business name</label>
              <input
                id="businessName"
                type="text"
                formControlName="businessName"
                autocomplete="organization"
                placeholder="e.g. Vijay Jewellers"
                (input)="onBusinessNameInput($event)"
                (blur)="onBusinessNameBlur()"
              />
            </div>

            <div class="field">
              <label class="required-label" for="businessEntityName">Legal entity name</label>
              <input
                id="businessEntityName"
                type="text"
                formControlName="businessEntityName"
                placeholder="Enter legal entity name"
              />
            </div>

            <div class="field full-width">
              <label class="required-label" for="businessAddressLine1">Address line 1</label>
              <input
                id="businessAddressLine1"
                type="text"
                formControlName="businessAddressLine1"
                autocomplete="address-line1"
                placeholder="Building, street, or area"
              />
            </div>

            <div class="field full-width">
              <label for="businessAddressLine2">Address line 2 <small>Optional</small></label>
              <input
                id="businessAddressLine2"
                type="text"
                formControlName="businessAddressLine2"
                autocomplete="address-line2"
                placeholder="Landmark, floor, or suite"
              />
            </div>

            <div class="field">
              <label class="required-label" for="businessCity">City</label>
              <input
                id="businessCity"
                type="text"
                formControlName="businessCity"
                autocomplete="address-level2"
                placeholder="Enter city"
              />
            </div>

            <div class="field">
              <label class="required-label" for="businessState">State</label>
              <input
                id="businessState"
                type="text"
                formControlName="businessState"
                autocomplete="address-level1"
                placeholder="Enter state"
              />
            </div>
          </div>
        </section>

        @if (showStoreDetails()) {
          <section class="form-section store-section">
            <div class="section-title">
              <span><mat-icon>storefront</mat-icon></span>
              <div>
                <h3>Store information</h3>
                <p>Enter the address customers will visit.</p>
              </div>
            </div>

            <div class="form-grid">
              <div class="field full-width">
                <label class="required-label" for="storeName">Store name</label>
                <input
                  id="storeName"
                  type="text"
                  formControlName="storeName"
                  placeholder="e.g. Main Showroom"
                />
              </div>

              <div class="field full-width">
                <label class="required-label" for="storeAddressLine1">Address line 1</label>
                <input
                  id="storeAddressLine1"
                  type="text"
                  formControlName="storeAddressLine1"
                  autocomplete="address-line1"
                  placeholder="Building, street, or area"
                />
              </div>

              <div class="field full-width">
                <label for="storeAddressLine2">Address line 2 <small>Optional</small></label>
                <input
                  id="storeAddressLine2"
                  type="text"
                  formControlName="storeAddressLine2"
                  autocomplete="address-line2"
                  placeholder="Landmark, floor, or suite"
                />
              </div>

              <div class="field">
                <label class="required-label" for="storeCity">City</label>
                <input
                  id="storeCity"
                  type="text"
                  formControlName="storeCity"
                  autocomplete="address-level2"
                  placeholder="Enter city"
                />
              </div>

              <div class="field">
                <label class="required-label" for="storeState">State</label>
                <input
                  id="storeState"
                  type="text"
                  formControlName="storeState"
                  autocomplete="address-level1"
                  placeholder="Enter state"
                />
              </div>

              <div class="field full-width">
                <label for="googleMapsLink">Google Maps link <small>Optional</small></label>
                <div class="icon-input">
                  <mat-icon>link</mat-icon>
                  <input
                    id="googleMapsLink"
                    type="url"
                    formControlName="googleMapsLink"
                    placeholder="Paste your Google Maps location link"
                  />
                </div>
              </div>
            </div>
          </section>
        }
      </div>

      <label class="same-address-card">
        <input type="checkbox" formControlName="sameAddressForStore" />
        <span class="checkbox-ui"><mat-icon>check</mat-icon></span>
        <span>
          <strong>Use this address for my store</strong>
          <small>No separate store details will be required.</small>
        </span>
      </label>

      <footer class="form-actions">
        <div class="privacy-note"><mat-icon>shield</mat-icon> Your details are securely stored</div>
        <button class="submit-button" type="submit" [disabled]="!canSubmit()">
          <span>{{ isSubmitting() ? 'Creating business…' : 'Create business' }}</span>
          <mat-icon>arrow_forward</mat-icon>
        </button>
      </footer>
    </form>
  </section>

  @if (setupComplete()) {
    <div class="success-backdrop">
      <section class="success-dialog" role="dialog" aria-modal="true" aria-labelledby="setup-success-title">
        <button class="dialog-close" type="button" aria-label="Close" (click)="closeSuccess()">
          <mat-icon>close</mat-icon>
        </button>
        <div #successAnimation class="success-animation" aria-hidden="true"></div>
        <span class="success-label">Setup complete</span>
        <h2 id="setup-success-title">Your business is ready!</h2>
        <p class="success-message">
          Your business and primary store have been created successfully.
        </p>
        <div class="success-summary">
          <div class="summary-heading">
            <span><mat-icon>business</mat-icon></span>
            <div>
              <small>Business created</small>
              <strong>{{ createdBusiness().businessName }}</strong>
            </div>
          </div>
          <div class="summary-grid">
            <div>
              <small>Legal entity</small>
              <strong>{{ createdBusiness().legalEntityName }}</strong>
            </div>
            <div>
              <small>Primary store</small>
              <strong>{{ createdBusiness().storeName }}</strong>
            </div>
            <div class="summary-location">
              <small>Location</small>
              <strong><mat-icon>location_on</mat-icon>{{ createdBusiness().location }}</strong>
            </div>
          </div>
        </div>
        <button class="submit-button dashboard-button" type="button" (click)="goToDashboard()">
          <span>Go to dashboard</span>
          <mat-icon>arrow_forward</mat-icon>
        </button>
      </section>
    </div>
  }
</main>
